/**
 * Horographia — watch & brand catalog (educational).
 * Piece photos: studio cutouts (multi-angle). Commons pages kept as silhouette reference.
 * Prices are rough educational retail bands (current + launch-era scale) — not appraisals or live quotes.
 */

export type CommonsImage = {
  src: string;
  alt: string;
  credit: string;
  license: string;
  commonsPage: string;
};

export type ViewAngle = 'front' | 'tq' | 'side';

export type PieceView = {
  angle: ViewAngle;
  src: string;
  labelKo: string;
};

const ANGLE_LABEL: Record<ViewAngle, string> = {
  front: '정면',
  tq: '사선',
  side: '측면',
};

const ANGLES: ViewAngle[] = ['front', 'tq', 'side'];

const ASSET_REV = '20260908g';
function viewsFor(id: string): PieceView[] {
  return ANGLES.map((angle) => ({
    angle,
    src: `/pieces/${id}/${angle}.webp?v=${ASSET_REV}`,
    labelKo: ANGLE_LABEL[angle],
  }));
}

function studio(id: string, alt: string, commonsPage: string): CommonsImage {
  return {
    // Deck strip uses small thumbs; stage loads full angle WebPs via viewsFor.
    src: `/pieces/${id}/tq.thumb.webp?v=${ASSET_REV}`,
    alt,
    credit: 'Horographia studio cutout (Commons silhouette reference)',
    license: 'Original artwork',
    commonsPage,
  };
}

export type Brand = {
  id: string;
  name: string;
  nameKo: string;
  country: string;
  founded: string;
  motto: string;
  summary: string;
  signature: string[];
  image: CommonsImage;
};

/** 수제작 성격 — 완전 수제 vs 매뉴팩처 마감 vs 산업 라인 */
export type CraftKind = 'industrial' | 'manufacture-finish' | 'atelier';

export type WatchPiece = {
  id: string;
  brandId: string;
  name: string;
  nameKo: string;
  /** 라인/대표 모델 출시·공개 연도 (교육용) */
  year: string;
  caliber: string;
  caseMm: string;
  summary: string;
  /** 특장점 */
  highlights: string[];
  /** 기술·기능 */
  technology: string[];
  /** 교육용 대략 현행 정가 구간 (통화·시세 변동 있음) */
  priceBand: string;
  /** 교육용 출시·초기 공개 당시 정가 스케일 (기록·환산 불완전, 시세·감정 아님) */
  launchPrice: string;
  craft: CraftKind;
  craftNote: string;
  metal: string;
  dial: string;
  accent: string;
  image: CommonsImage;
  views: PieceView[];
};

export const CRAFT_LABEL: Record<CraftKind, string> = {
  industrial: '산업 라인',
  'manufacture-finish': '매뉴팩처 + 수작업 마감',
  atelier: '아틀리에·고난도 수작업 비중 큼',
};

export const brands: Brand[] = [
  {
    id: 'rolex',
    name: 'Rolex',
    nameKo: '롤렉스',
    country: '스위스',
    founded: '1905',
    motto: '정밀과 방수를 일상으로',
    summary:
      '오토매틱과 방수 케이스의 대중화에 큰 영향을 준 제네바 기반 브랜드. 서브마리너·데이토나 등 스포츠 라인으로 널리 알려졌다.',
    signature: ['서브마리너', '데이토나', '데이트저스트'],
    image: {
      src: 'https://upload.wikimedia.org/wikipedia/commons/thumb/f/f4/Logo_da_Rolex.png/960px-Logo_da_Rolex.png',
      alt: 'Rolex 워드마크',
      credit: 'Hans Wilsdorf / Wikimedia Commons',
      license: 'Public domain',
      commonsPage: 'https://commons.wikimedia.org/wiki/File:Logo_da_Rolex.png',
    },
  },
  {
    id: 'omega',
    name: 'Omega',
    nameKo: '오메가',
    country: '스위스',
    founded: '1848',
    motto: '달 착륙과 크로노그래프',
    summary:
      '스위스 비엔 기반. 스피드마스터는 유인 우주 비행에 사용된 기록으로 유명하며, 코액시얼 이스케이프먼트 연구로도 알려져 있다.',
    signature: ['스피드마스터', '심마스터', '콘스텔레이션'],
    image: {
      src: 'https://upload.wikimedia.org/wikipedia/commons/thumb/f/fb/Omega_Logo.svg/960px-Omega_Logo.svg.png',
      alt: 'Omega 로고',
      credit: 'Omega / Wikimedia Commons',
      license: 'Public domain',
      commonsPage: 'https://commons.wikimedia.org/wiki/File:Omega_Logo.svg',
    },
  },
  {
    id: 'patek',
    name: 'Patek Philippe',
    nameKo: '파텍 필립',
    country: '스위스',
    founded: '1839',
    motto: '컴플리케이션의 기준점',
    summary:
      '제네바 하이엔드 매뉴팩처. 퍼페추얼 캘린더·미닛 리피터 등 복잡한 기능과 칼라트라바 라인으로 수집가 사이에서 자주 언급된다.',
    signature: ['칼라트라바', '노틸러스', '아쿠아넛', '그랜드 컴플리케이션'],
    image: {
      src: 'https://upload.wikimedia.org/wikipedia/commons/0/02/Patek_Philippe_Logo.png',
      alt: 'Patek Philippe 로고',
      credit: 'Patek Philippe / Wikimedia Commons',
      license: 'Public domain',
      commonsPage: 'https://commons.wikimedia.org/wiki/File:Patek_Philippe_Logo.png',
    },
  },
  {
    id: 'ap',
    name: 'Audemars Piguet',
    nameKo: '오데마 피게',
    country: '스위스',
    founded: '1875',
    motto: '로열 오크와 르브라쥐',
    summary:
      '쥐라 산맥 르브라쥐 마을 기반. 1972년 로열 오크의 옥타곤 베젤·통합 메탈 브레이슬릿이 스포츠 럭셔리의 한 축이 되었다.',
    signature: ['로열 오크', '로열 오크 오프쇼어', '코드 11.59'],
    image: {
      src: 'https://upload.wikimedia.org/wikipedia/commons/thumb/7/7c/Audemars_Piguet_logo.png/960px-Audemars_Piguet_logo.png',
      alt: 'Audemars Piguet 로고',
      credit: 'Audemars Piguet / Wikimedia Commons',
      license: 'Public domain',
      commonsPage: 'https://commons.wikimedia.org/wiki/File:Audemars_Piguet_logo.png',
    },
  },
  {
    id: 'cartier',
    name: 'Cartier',
    nameKo: '까르띠에',
    country: '프랑스 / 스위스',
    founded: '1847',
    motto: '주얼리 하우스의 시계 형태',
    summary:
      '파리에서 출발한 주얼리·시계 하우스. 탱크·산토스 등 기하학적 케이스 디자인이 20세기 손목시계 미학에 큰 영향을 주었다.',
    signature: ['탱크', '산토스', '발롱 블루'],
    image: {
      src: 'https://upload.wikimedia.org/wikipedia/commons/thumb/8/86/Cartier_logo.svg/960px-Cartier_logo.svg.png',
      alt: 'Cartier 로고',
      credit: 'Cartier / Wikimedia Commons',
      license: 'Public domain',
      commonsPage: 'https://commons.wikimedia.org/wiki/File:Cartier_logo.svg',
    },
  },
  {
    id: 'seiko',
    name: 'Seiko',
    nameKo: '세이코',
    country: '일본',
    founded: '1881',
    motto: '쿼츠에서 스프링드라이브까지',
    summary:
      '도쿄 기반. 1969년 쿼츠 아스트론, 이후 그랜드 세이코·스프링드라이브로 기계식과 전자식의 경계를 연구한 메이커로 알려져 있다.',
    signature: ['프로스펙스', '프레사지', '그랜드 세이코'],
    image: {
      src: 'https://upload.wikimedia.org/wikipedia/commons/thumb/2/2c/Seiko_logo.svg/960px-Seiko_logo.svg.png',
      alt: 'Seiko 로고',
      credit: 'Seiko / Wikimedia Commons',
      license: 'Public domain',
      commonsPage: 'https://commons.wikimedia.org/wiki/File:Seiko_logo.svg',
    },
  },
  {
    id: 'jlc',
    name: 'Jaeger-LeCoultre',
    nameKo: '예거 르쿨트르',
    country: '스위스',
    founded: '1833',
    motto: '리버소와 매뉴팩처 칼리버',
    summary:
      '발레드주 매뉴팩처. 뒤집히는 케이스의 리버소, 메모복스 알람 등 독특한 기구 설계로 유명하다.',
    signature: ['리버소', '마스터 컨트롤', '폴라리스'],
    image: {
      src: 'https://upload.wikimedia.org/wikipedia/commons/thumb/d/df/Jaeger-LeCoultre_Logo.png/960px-Jaeger-LeCoultre_Logo.png',
      alt: 'Jaeger-LeCoultre 로고',
      credit: 'Jaeger-LeCoultre / Wikimedia Commons',
      license: 'Public domain',
      commonsPage: 'https://commons.wikimedia.org/wiki/File:Jaeger-LeCoultre_Logo.png',
    },
  },
  {
    id: 'iwc',
    name: 'IWC Schaffhausen',
    nameKo: 'IWC 샤프하우젠',
    country: '스위스',
    founded: '1868',
    motto: '파일럿과 포르투갈 라인',
    summary:
      '샤프하우젠 기반. 파일럿 워치·포르투기저 등 도구적 가독성과 큰 다이얼 전통을 이어 온 브랜드다.',
    signature: ['파일럿', '포르투기저', '포르토피노'],
    image: {
      src: 'https://upload.wikimedia.org/wikipedia/commons/thumb/f/ff/IWC_logo.svg/960px-IWC_logo.svg.png',
      alt: 'IWC 로고',
      credit: 'IWC / Wikimedia Commons',
      license: 'Public domain',
      commonsPage: 'https://commons.wikimedia.org/wiki/File:IWC_logo.svg',
    },
  },
  {
    id: 'vc',
    name: 'Vacheron Constantin',
    nameKo: '바쉐론 콘스탄틴',
    country: '스위스',
    founded: '1755',
    motto: '현존 최장수 시계 매뉴팩처 중 하나',
    summary:
      '제네바. 장식 마감과 슬림 드레스 워치, 오버시즈 스포츠 라인으로 하이엔드 스펙트럼을 넓혀 왔다.',
    signature: ['파트리모니', '오버시즈', '트래디션넬'],
    image: {
      src: 'https://upload.wikimedia.org/wikipedia/commons/e/e8/Vacheron_Constantin_logo.png',
      alt: 'Vacheron Constantin 로고',
      credit: 'Richemont International SA / Wikimedia Commons',
      license: 'Public domain',
      commonsPage: 'https://commons.wikimedia.org/wiki/File:Vacheron_Constantin_logo.png',
    },
  },
  {
    id: 'longines',
    name: 'Longines',
    nameKo: '론진',
    country: '스위스',
    founded: '1832',
    motto: '스포츠 타임키핑과 클래식',
    summary:
      '생티미에 기반. 항공·스포츠 계측 역사와 하이드로콘퀘스트·마스터 컬렉션 등 접근성 있는 스위스 라인으로 알려져 있다.',
    signature: ['하이드로콘퀘스트', '마스터 컬렉션', '스피릿'],
    image: {
      src: 'https://upload.wikimedia.org/wikipedia/commons/thumb/0/0a/Longines_wordmark_logo.svg/960px-Longines_wordmark_logo.svg.png',
      alt: 'Longines 워드마크',
      credit: 'Longines Watch Co. Francillon Ltd. / Wikimedia Commons',
      license: 'Public domain',
      commonsPage: 'https://commons.wikimedia.org/wiki/File:Longines_wordmark_logo.svg',
    },
  },
];

function piece(
  partial: Omit<WatchPiece, 'views' | 'image'> & { commonsPage: string; alt: string },
): WatchPiece {
  const { commonsPage, alt, ...rest } = partial;
  return {
    ...rest,
    image: studio(rest.id, alt, commonsPage),
    views: viewsFor(rest.id),
  };
}

export const pieces: WatchPiece[] = [
  piece({
    id: 'submariner',
    brandId: 'rolex',
    name: 'Submariner',
    nameKo: '서브마리너',
    year: '1953 공개 (현행 라인은 세대별)',
    caliber: '오토매틱 (시대별 칼리버, 현행 예: 3235 계열)',
    caseMm: '약 40–41 mm',
    summary: '다이빙용 방수·야광 표시를 전제로 한 스포츠 아이콘. 회전 베젤과 견고한 케이스 구조가 특징이다.',
    highlights: ['다이빙 도구에서 출발한 아이콘', '단방향 회전 베젤', '오이스터 케이스·브레이슬릿 전통'],
    technology: ['스크류 다운 크라운', '회전 베젤(경과 시간)', '야광 인덱스·침', '방수 스포츠 케이스'],
    priceBand: '교육용 현행 대략 정가: 강철 현행 약 1만 달러대 (시세·그레이는 별개)',
    launchPrice: '교육용 출시 스케일: 1953년 전후 강철 초기 정가 약 150~210달러대 인용 (ref·시장마다 다름). 당시 전문 다이버용 도구 가격대.',
    craft: 'industrial',
    craftNote: '완전 수제가 아니라 매뉴팩처 산업 라인. 부품·조립은 엄격한 공정 관리.',
    metal: '#c0c4c8',
    dial: '#0b1f2a',
    accent: '#1f6b4a',
    alt: '서브마리너 실루엣 스튜디오 누끼',
    commonsPage: 'https://commons.wikimedia.org/wiki/File:Rolex-Submariner.jpg',
  }),
  piece({
    id: 'daytona',
    brandId: 'rolex',
    name: 'Cosmograph Daytona',
    nameKo: '코스모그래프 데이토나',
    year: '1963 공개',
    caliber: '크로노그래프 오토매틱 (현행 예: 4130/4131 계열)',
    caseMm: '약 40 mm',
    summary: '레이싱 타이밍용 크로노그래프. 타키미터 베젤과 서브다이얼 레이아웃이 시그니처다.',
    highlights: ['타키미터 베젤', '모터스포츠 이미지', '스크류 다운 푸셔·크라운'],
    technology: ['크로노그래프(시작·정지·리셋)', '타키미터 스케일', '서브다이얼 카운터', '오이스터 방수 케이스'],
    priceBand: '교육용 현행 대략 정가: 강철 현행 약 1.5만~1.7만 달러대 (시세는 크게 변동)',
    launchPrice: '교육용 출시 스케일: 1963년 전후 초기 수동 크로노 정가 약 250~300달러대 인용. 현행 오토·시세와는 차원이 다름.',
    craft: 'industrial',
    craftNote: '수제작 아틀리에 제품이 아님. 자체 칼리버·엄격한 생산 라인.',
    metal: '#d8d2c4',
    dial: '#111111',
    accent: '#b33a2b',
    alt: '데이토나 실루엣 스튜디오 누끼',
    commonsPage: 'https://commons.wikimedia.org/wiki/File:Rolex_Daytona_Cosmograph.jpg',
  }),
  piece({
    id: 'speedmaster',
    brandId: 'omega',
    name: 'Speedmaster Professional',
    nameKo: '스피드마스터 프로페셔널',
    year: '1957 기원 / 달착륙 계열로 유명',
    caliber: '수동 크로노그래프 (대표: 1861 등 계열)',
    caseMm: '약 42 mm',
    summary: 'NASA 유인 우주 임무와 연결된 크로노그래프. 헤살라이트 크리스탈·트위스트 러그 등 디테일이 많다.',
    highlights: ['우주 비행 기록', '수동 크로노 전통', '타키미터 베젤'],
    technology: ['수동 와인딩 크로노그래프', '타키미터', '헤살라이트(전통 사양) / 사파이어 변형', '트위스트 러그'],
    priceBand: '교육용 현행 대략 정가: Moonwatch 강철 약 6천~8천 달러대',
    launchPrice: '교육용 출시 스케일: 1950년대 말~60년대 초 크로노 정가 약 200달러 전후 인용(초기 CK 계열). 달착륙 서사 이전의 스포츠 크로노 가격대.',
    craft: 'manufacture-finish',
    craftNote: '매뉴팩처 생산. 완전 수제는 아니나 무브먼트·케이스 마감에 수작업 공정이 포함된다.',
    metal: '#9aa0a6',
    dial: '#0a0a0a',
    accent: '#e8e8e8',
    alt: '스피드마스터 실루엣 스튜디오 누끼',
    commonsPage: 'https://commons.wikimedia.org/wiki/File:Omega_Speedmaster_Schumacher_Edition_.jpeg',
  }),
  piece({
    id: 'seamaster',
    brandId: 'omega',
    name: 'Seamaster',
    nameKo: '심마스터',
    year: '1948 라인 시작',
    caliber: '오토매틱 / 코액시얼 계열',
    caseMm: '모델별',
    summary: '다이빙·트래블 라인을 아우르는 오메가의 대표 스포츠 패밀리. 웨이브 다이얼이 자주 등장한다.',
    highlights: ['다이빙·트래블 패밀리', '웨이브 다이얼', '코액시얼 채택 모델 다수'],
    technology: ['코액시얼 이스케이프먼트(해당 모델)', '헬륨 밸브(일부 다이버)', '방수 다이빙 케이스', '야광 표시'],
    priceBand: '교육용 현행 대략 정가: 다이버 현행 약 5천~8천 달러대',
    launchPrice: '교육용 출시 스케일: 1948년 라인 시작. 초기는 드레스·트래블 성격이라 모델마다 편차가 큼. 당시 중상급 손목시계 정가대(수백 달러 미만 인용 다수).',
    craft: 'manufacture-finish',
    craftNote: '산업 매뉴팩처. 수제 특제품이 아님.',
    metal: '#b8bcc0',
    dial: '#123a5c',
    accent: '#f0c75e',
    alt: '심마스터 실루엣 스튜디오 누끼',
    commonsPage: 'https://commons.wikimedia.org/wiki/File:Omega_Seamaster_300_militare.jpg',
  }),
  piece({
    id: 'nautilus',
    brandId: 'patek',
    name: 'Nautilus',
    nameKo: '노틸러스',
    year: '1976 공개 (제라드 제엔타)',
    caliber: '오토매틱',
    caseMm: '모델별 (대표 약 40 mm대)',
    summary: '제라드 제엔타 디자인의 포르톨 케이스 스포츠 럭셔리. 수평 엠보스 다이얼이 상징적이다.',
    highlights: ['포르톨(타원형) 케이스', '통합 브레이슬릿', '수평 엠보스 다이얼'],
    technology: ['오토매틱 칼리버', '날짜·요일 등 모델별 컴플리케이션', '스포츠 럭셔리 방수', '통합 메탈 브레이슬릿'],
    priceBand: '교육용 현행 대략 정가: 강철 현행 수천만~억 원대 (시세는 정가와 크게 괴리될 수 있음)',
    launchPrice: '교육용 출시 스케일: 1976년 강철 초기 정가 약 2,400~3,300달러(또는 유사 CHF)대 인용. 당시에도 강철 럭셔리로 비쌌고, 오늘날 시세와는 별개.',
    craft: 'manufacture-finish',
    craftNote: '하이엔드 매뉴팩처. 무브먼트·마감에 수작업 비중이 크지만 「한 사람이 통째로 깎는 수제」는 아님.',
    metal: '#aeb4ba',
    dial: '#2a4a6e',
    accent: '#d4af37',
    alt: '노틸러스 실루엣 스튜디오 누끼',
    commonsPage: 'https://commons.wikimedia.org/wiki/File:Patek-Philippe-Nautilus-5711.jpg',
  }),
  piece({
    id: 'calatrava',
    brandId: 'patek',
    name: 'Calatrava',
    nameKo: '칼라트라바',
    year: '1932 라인 시작',
    caliber: '수동 / 오토매틱',
    caseMm: '모델별',
    summary: '라운드 드레스 워치의 전형. 얇은 케이스와 절제된 인덱스가 특징이다.',
    highlights: ['드레스 워치의 전형', '절제된 다이얼', '90년 넘는 라인'],
    technology: ['슬림 케이스 설계', '수동·오토 선택지', '클래식 시·분·초(모델별 데이트)', '고급 마감'],
    priceBand: '교육용 현행 대략 정가: 모델별 약 2만~5만 달러대+',
    launchPrice: '교육용 출시 스케일: 1932년 전후 골드 드레스 워치. 현대 달러 환산은 의미 없고, 당시 상류층 드레스 시계 정가대. 현행 모델 정가와 직접 비교 불가.',
    craft: 'manufacture-finish',
    craftNote: '매뉴팩처 마감. 그랜드 컴플리케이션급은 아틀리에 성격이 더 강해진다.',
    metal: '#d7c59a',
    dial: '#f4f0e6',
    accent: '#1a1a1a',
    alt: '칼라트라바 실루엣 스튜디오 누끼',
    commonsPage: 'https://commons.wikimedia.org/wiki/File:Patek_Philippe_Calatrava_ref._5055.jpg',
  }),
  piece({
    id: 'royal-oak',
    brandId: 'ap',
    name: 'Royal Oak',
    nameKo: '로열 오크',
    year: '1972 공개',
    caliber: '오토매틱',
    caseMm: '모델별 (대표 39–41 mm대)',
    summary: '옥타곤 베젤과 노출 스크류, 탭isserie 다이얼. 강철 럭셔리 스포츠의 이정표로 자주 인용된다.',
    highlights: ['옥타곤 베젤·노출 스크류', '탭isserie 다이얼', '통합 브레이슬릿'],
    technology: ['오토매틱', '일체형 메탈 브레이슬릿', '브러시·폴리시 교차 마감', '스포츠 방수'],
    priceBand: '교육용 현행 대략 정가: 강철 기본 약 2만 달러대+ (시세 변동 큼)',
    launchPrice: '교육용 출시 스케일: 1972년 강철 초기 정가 약 3,300CHF대 인용. 당시 강철치고 파격적으로 비싸 「강철 럭셔리」 논쟁의 출발점으로 자주 언급.',
    craft: 'manufacture-finish',
    craftNote: '르브라쥐 매뉴팩처. 케이스·브레이슬릿 마감에 수작업이 많지만 완전 수제 한 점 제작은 아님.',
    metal: '#c5c9ce',
    dial: '#4a5560',
    accent: '#8a9199',
    alt: '로열 오크 실루엣 스튜디오 누끼',
    commonsPage: 'https://commons.wikimedia.org/wiki/File:Audemars_Piguet_Royal_Oak_ref._15202.jpg',
  }),
  piece({
    id: 'tank',
    brandId: 'cartier',
    name: 'Tank',
    nameKo: '탱크',
    year: '1917 설계 / 1919 공개로 알려짐',
    caliber: '쿼츠 / 수동 / 오토매틱',
    caseMm: '직사각 (사이즈 다양)',
    summary: '전차 궤도에서 영감을 받았다고 전해지는 직사각 케이스. 주얼리 하우스 시계의 대표 형태다.',
    highlights: ['직사각 실루엣', '주얼리 하우스 미학', '롱런 디자인'],
    technology: ['직사각 케이스·레일 브랜치', '쿼츠·기계식 선택', '교체형 스트랩(모델별)', '드레스 워치 비율'],
    priceBand: '교육용 현행 대략 정가: Must/솔로 쿼츠 수백만 원대 ~ 하이엔드 기계식 수천만 원대',
    launchPrice: '교육용 출시 스케일: 1919년 전후 공개. 초기 기록은 주얼리 하우스 맞춤·소량에 가깝고 단일 양산 정가가 불명확. 1차대전 직후 고급 손목시계대.',
    craft: 'industrial',
    craftNote: '모델에 따라 쿼츠 산업 라인부터 매뉴팩처 기계식까지. 기본 Tank는 수제 특제품이 아님.',
    metal: '#c9a84c',
    dial: '#f7f3ea',
    accent: '#1c1c1c',
    alt: '탱크 실루엣 스튜디오 누끼',
    commonsPage: 'https://commons.wikimedia.org/wiki/File:Cartier_Tank.jpg',
  }),
  piece({
    id: 'santos',
    brandId: 'cartier',
    name: 'Santos',
    nameKo: '산토스',
    year: '1904 기원 (초기 파일럿용 손목시계로 언급)',
    caliber: '오토매틱 / 쿼츠',
    caseMm: '모델별',
    summary: '초기 파일럿용 손목시계로 언급되는 스퀘어 케이스. 노출 스크류 베젤이 시그니처다.',
    highlights: ['스퀘어 케이스', '노출 스크류 베젤', '항공 서사'],
    technology: ['스퀘어 케이스', '퀵스위치 브레이슬릿(현행 일부)', '오토·쿼츠', '스크류 모티브 디자인'],
    priceBand: '교육용 현행 대략 정가: 강철 오토 약 7천~1만 달러대',
    launchPrice: '교육용 출시 스케일: 1904년 기원. 초기 항공·맞춤 성격이 강해 「정찰용 정가표」 개념이 약함. 현대 양산 Santos 정가와는 별 세대.',
    craft: 'industrial',
    craftNote: '주얼리·시계 하우스의 시리즈 생산. 완전 수제 아님.',
    metal: '#b0b4b8',
    dial: '#ece6da',
    accent: '#8b1e1e',
    alt: '산토스 실루엣 스튜디오 누끼',
    commonsPage: 'https://commons.wikimedia.org/wiki/File:Cartier_Santos_wristwatch.jpg',
  }),
  piece({
    id: 'reverso',
    brandId: 'jlc',
    name: 'Reverso',
    nameKo: '리버소',
    year: '1931 공개',
    caliber: '수동 / 오토매틱',
    caseMm: '직사각',
    summary: '폴로 경기에서 크리스탈을 보호하기 위해 뒤집을 수 있는 케이스. 아르데코 비례가 강하다.',
    highlights: ['플립(뒤집기) 케이스', '아르데코 비례', '양면 다이얼·각인 활용'],
    technology: ['스위블 케이스', '양면 표시(모델별)', '수동·오토 칼리버', '슬림 직사각 무브먼트'],
    priceBand: '교육용 현행 대략 정가: 클래식 약 7천~1.5만 달러대+',
    launchPrice: '교육용 출시 스케일: 1931년 전후 폴로용 사각 플립 케이스. 당시 스포츠·럭셔리 중간~상급 정가대(수백 달러/프랑대 인용).',
    craft: 'manufacture-finish',
    craftNote: '발레드주 매뉴팩처. 케이스 기구와 마감에 수작업이 들어가지만 대량 시리즈 생산.',
    metal: '#d4c4a0',
    dial: '#1a1a1a',
    accent: '#e8dcc8',
    alt: '리버소 실루엣 스튜디오 누끼',
    commonsPage: 'https://commons.wikimedia.org/wiki/File:Jaeger-LeCoultre-Reverso.jpg',
  }),
  piece({
    id: 'prospex',
    brandId: 'seiko',
    name: 'Prospex diver',
    nameKo: '프로스펙스 다이버',
    year: '현대 라인 (유산은 1960년대 다이버부터)',
    caliber: '오토매틱 / 솔라 등',
    caseMm: '모델별',
    summary: '세이코의 다이빙·필드 도구 라인. 터틀·사무라이 등 케이스 실루엣이 다양하다.',
    highlights: ['다이빙 도구 지향', '다양한 케이스 실루엣', '접근성 있는 가격대'],
    technology: ['회전 베젤', 'ISO 다이버 사양(해당 모델)', '오토·솔라·키네틱 등', '야광 표시'],
    priceBand: '교육용 현행 대략 정가: 엔트리~중급 약 수백~2천 달러대',
    launchPrice: '교육용 출시 스케일: 현대 Prospex 라인은 모델마다 다름. 엔트리~중급 다이버 기준 출시 정가 대략 수백~2천달러대(해당 연도 MSRP).',
    craft: 'industrial',
    craftNote: '대량 생산 산업 라인. 수제작 아님. (그랜드 세이코는 별도 등급)',
    metal: '#8e949a',
    dial: '#0d2b24',
    accent: '#f2b705',
    alt: '프로스펙스 실루엣 스튜디오 누끼',
    commonsPage: 'https://commons.wikimedia.org/wiki/File:Seiko_Prospex_SPB143J1.png',
  }),
  piece({
    id: 'pilot',
    brandId: 'iwc',
    name: 'Pilot’s Watch',
    nameKo: '파일럿 워치',
    year: '1936 항공 시계 기원 ~ (빅 파일럿 등은 이후 세대)',
    caliber: '오토매틱 (예: 50000 계열 장파워 리저브)',
    caseMm: '모델별 (빅 파일럿은 큰 다이얼·두꺼운 케이스)',
    summary:
      '장갑을 낀 상태의 가독성을 전제로 한 항공 도구 전통. 삼각형 인덱스·큰 양파형 크라운·두꺼운 측면 실루엣이 특징이다.',
    highlights: ['항공 가독성', '대형 양파형 크라운', '긴 파워 리저브(해당 모델)', '두꺼운 케이스 프로파일'],
    technology: [
      '오토매틱',
      '파워 리저브 표시(DAYS 서브다이얼 등)',
      '야광·큰 아라비아 숫자',
      '소프트아이언 이너 케이스(일부 안티마그네틱 모델)',
    ],
    priceBand: '교육용 현행 대략 정가: 빅 파일럿 강철 약 1만~1.5만 달러대',
    launchPrice: '교육용 출시 스케일: 1930년대 항공 관찰시계와 현대 Big Pilot는 별 세대. 현대 BP급 초기 공개 정가는 대략 수천~1만달러대 인용.',
    craft: 'manufacture-finish',
    craftNote: '샤프하우젠 매뉴팩처. 산업·매뉴팩처 생산이며 완전 수제 한 점 제작은 아님.',
    metal: '#9c9fa3',
    dial: '#111111',
    accent: '#f5f5f5',
    alt: '파일럿 실루엣 스튜디오 누끼',
    commonsPage: 'https://commons.wikimedia.org/wiki/File:IWC_Big_Pilot_St_Exupery_Edition_(cropped).jpg',
  }),
  piece({
    id: 'datejust',
    brandId: 'rolex',
    name: 'Datejust',
    nameKo: '데이트저스트',
    year: '1945 공개',
    caliber: '오토매틱 (현행 예: 3235 계열)',
    caseMm: '약 36–41 mm (세대·사이즈별)',
    summary:
      '항상 보이는 날짜 창과 사이클로프스 렌즈로 유명한 드레스·데일리 라인. 플루티드 베젤·주빌리·오이스터 브레이슬릿 조합이 많다.',
    highlights: ['날짜 + 사이클로프스', '플루티드 베젤 이미지', '롱런 데일리 워치'],
    technology: ['오토매틱', '데이트 창', '사이클로프스 렌즈', '스크류 다운 크라운'],
    priceBand: '교육용 현행 대략 정가: 강철 현행 약 8천~1.2만 달러대',
    launchPrice: '교육용 출시 스케일: 1945년 전후. 날짜 창+오토를 넣은 중상급 정가대(전후 수백 달러대 인용). 현행 DJ41 정가와는 세대가 다름.',
    craft: 'industrial',
    craftNote: '매뉴팩처 산업 라인. 완전 수제 아님.',
    metal: '#c8ccd0',
    dial: '#1e3a5f',
    accent: '#c9a227',
    alt: '데이트저스트 실루엣 스튜디오 누끼',
    commonsPage: 'https://commons.wikimedia.org/wiki/Category:Rolex_Datejust',
  }),
  piece({
    id: 'gmt-master',
    brandId: 'rolex',
    name: 'GMT-Master',
    nameKo: 'GMT 마스터',
    year: '1955 공개',
    caliber: '오토매틱 GMT (현행 예: 3285 계열)',
    caseMm: '약 40 mm',
    summary:
      '24시간 바늘과 양면 색 베젤로 두 시간대를 읽는 트래블·항공 도구. 「펩시」「배트맨」 등 베젤 색 별명이 많다.',
    highlights: ['GMT 바늘', '24시간 회전 베젤', '항공·여행 서사'],
    technology: ['GMT/듀얼 타임', '양방향 또는 독립 시침(세대별)', '세라믹 베젤 인서트(현행)', '오이스터 방수'],
    priceBand: '교육용 현행 대략 정가: 강철 현행 약 1만~1.2만 달러대 (시세 별개)',
    launchPrice: '교육용 출시 스케일: 1955년 전후 항공·트래블용. 초기 정가 서브마리너와 비슷한 수백 달러대 인용(사양·시장 상이).',
    craft: 'industrial',
    craftNote: '산업 매뉴팩처 생산.',
    metal: '#b8bcc2',
    dial: '#0a0a0a',
    accent: '#c41e3a',
    alt: 'GMT 마스터 실루엣 스튜디오 누끼',
    commonsPage: 'https://commons.wikimedia.org/wiki/Category:Rolex_GMT-Master',
  }),
  piece({
    id: 'overseas',
    brandId: 'vc',
    name: 'Overseas',
    nameKo: '오버시즈',
    year: '1996 공개 (현행 세대는 이후 개량)',
    caliber: '오토매틱',
    caseMm: '모델별 (대표 약 41 mm대)',
    summary:
      '바쉐론의 스포츠 럭셔리. 말테 크로스 모티브 베젤과 통합 브레이슬릿, 빠른 스트랩 교환이 특징이다.',
    highlights: ['말테 크로스 베젤', '통합 브레이슬릿', '하이엔드 스포츠'],
    technology: ['오토매틱', '퀵체인지 스트랩(현행)', '사파이어 크리스탈', '스포츠 방수'],
    priceBand: '교육용 현행 대략 정가: 강철 약 2만 달러대+',
    launchPrice: '교육용 출시 스케일: 1996년 전후 강철 스포츠. 출시 정가 대략 중상급 매뉴팩처대(수천~1만달러대 초반 인용). 현행 세대와 스펙·가격표가 다름.',
    craft: 'manufacture-finish',
    craftNote: '하이엔드 매뉴팩처. 마감 수작업 비중은 크지만 시리즈 생산.',
    metal: '#c2c6cc',
    dial: '#1a4a6e',
    accent: '#d4af37',
    alt: '오버시즈 실루엣 스튜디오 누끼',
    commonsPage: 'https://commons.wikimedia.org/wiki/Category:Vacheron_Constantin',
  }),
  piece({
    id: 'hydroconquest',
    brandId: 'longines',
    name: 'HydroConquest',
    nameKo: '하이드로콘퀘스트',
    year: '2007 라인 (다이버 계열)',
    caliber: '오토매틱 / 쿼츠',
    caseMm: '약 39–43 mm',
    summary:
      '론진의 접근성 있는 다이버. 세라믹 베젤·야광·견고한 브레이슬릿으로 「입문 스위스 다이버」로 자주 소개된다.',
    highlights: ['입문 다이버', '세라믹 베젤', '가성비 스위스'],
    technology: ['단방향 회전 베젤', '스크류 다운 크라운', '야광', '방수 다이빙 사양(해당 모델)'],
    priceBand: '교육용 현행 대략 정가: 오토 약 1.5천~2.5천 달러대',
    launchPrice: '교육용 출시 스케일: 2007년 전후 엔트리 럭셔리 다이버. 출시 정가 대략 1천달러 전후~저수천달러대(모델별).',
    craft: 'industrial',
    craftNote: '스와치 그룹 산업 라인. 수제작 아님.',
    metal: '#9aa0a8',
    dial: '#0c2a3a',
    accent: '#3d9ad1',
    alt: '하이드로콘퀘스트 실루엣 스튜디오 누끼',
    commonsPage: 'https://commons.wikimedia.org/wiki/Category:Longines',
  }),
  piece({
    id: 'portugueser',
    brandId: 'iwc',
    name: 'Portugieser',
    nameKo: '포르투기저',
    year: '1939 기원 (현행 라인은 이후 부활·확장)',
    caliber: '오토매틱 / 수동 (크로노·퍼페추얼 등 다양)',
    caseMm: '약 40–44 mm대',
    summary:
      '철도 크로노미터에서 영감을 받은 큰 다이얼·아라비아 숫자. 파일럿보다 드레스에 가깝고, 미닛 트랙이 또렷하다.',
    highlights: ['큰 다이얼·철도 눈금', '클래식 아라비아', '크로노·캘린더 확장'],
    technology: ['오토·수동 선택', '크로노그래프(해당)', '파워 리저브 표시(해당)', '사파이어 전시백(다수)'],
    priceBand: '교육용 현행 대략 정가: 오토 타임온리 약 8천~1.2만 달러대',
    launchPrice: '교육용 출시 스케일: 1939년 기원은 특수·관찰용에 가깝고 양산 정가 기록이 드묾. 1990년대 이후 부활 라인의 출시 정가는 수천달러대+',
    craft: 'manufacture-finish',
    craftNote: '샤프하우젠 매뉴팩처 시리즈.',
    metal: '#d4c9b0',
    dial: '#f2ebe0',
    accent: '#1a1a1a',
    alt: '포르투기저 실루엣 스튜디오 누끼',
    commonsPage: 'https://commons.wikimedia.org/wiki/Category:IWC_watches',
  }),
  piece({
    id: 'constellation',
    brandId: 'omega',
    name: 'Constellation',
    nameKo: '콘스텔레이션',
    year: '1952 라인 시작',
    caliber: '오토매틱 / 코액시얼 (현행)',
    caseMm: '모델별',
    summary:
      '크로노미터 전통의 드레스·데일리 라인. 「클로」 러그·하프 바 인덱스·별자리 케이스백 모티브로 구분된다.',
    highlights: ['크로노미터 유산', '클로 러그', '코액시얼 현행'],
    technology: ['코액시얼(해당)', '날짜', '사파이어', '메탈·골드 케이스 선택'],
    priceBand: '교육용 현행 대략 정가: 강철 약 5천~8천 달러대',
    launchPrice: '교육용 출시 스케일: 1952년 전후 드레스·크로노미터 라인. 당시 중상급 정가대. 현행 코액시얼 세대와 직접 비교 어려움.',
    craft: 'manufacture-finish',
    craftNote: '오메가 매뉴팩처 라인.',
    metal: '#c5c0b5',
    dial: '#2a2a2a',
    accent: '#d4af37',
    alt: '콘스텔레이션 실루엣 스튜디오 누끼',
    commonsPage: 'https://commons.wikimedia.org/wiki/Category:Omega_watches',
  }),
  piece({
    id: 'aquanaut',
    brandId: 'patek',
    name: 'Aquanaut',
    nameKo: '아쿠아넛',
    year: '1997 공개',
    caliber: '오토매틱',
    caseMm: '모델별 (대표 약 40 mm대)',
    summary:
      '노틸러스의 동생 격 스포츠 라인. 둥근 팔각 케이스와 고무 스트랩, 엠보스 다이얼로 「캐주얼 하이엔드」 이미지를 만든다.',
    highlights: ['라운드 팔각 케이스', '컴포지트·고무 스트랩', '노틸러스 패밀리의 캐주얼 축'],
    technology: ['오토매틱', '날짜', '스포츠 방수', '사파이어'],
    priceBand: '교육용 현행 대략 정가: 강철 수천만 원대+ (시세 변동 큼)',
    launchPrice: '교육용 출시 스케일: 1997년 전후. 노틸러스보다 접근성 있게 포지셔닝했으나 여전히 하이엔드. 출시 정가 수천달러대~ 인용(소재·사이즈별).',
    craft: 'manufacture-finish',
    craftNote: '하이엔드 매뉴팩처 시리즈. 완전 수제 한 점 제작은 아님.',
    metal: '#b8bcc2',
    dial: '#1a3a4a',
    accent: '#e8a838',
    alt: '아쿠아넛 실루엣 스튜디오 누끼',
    commonsPage: 'https://commons.wikimedia.org/wiki/Category:Patek_Philippe',
  }),
  piece({
    id: 'grand-seiko',
    brandId: 'seiko',
    name: 'Grand Seiko',
    nameKo: '그랜드 세이코',
    year: '1960 라인 시작 (현행은 별도 브랜드 강조)',
    caliber: '하이비트 오토 / 스프링드라이브 등',
    caseMm: '모델별',
    summary:
      '세이코 그룹의 하이엔드. 자라츠 폴리시·뚜렷한 평면 절삭, 스프링드라이브의 흐르는 초침으로 「정밀의 미학」을 내세운다.',
    highlights: ['자라츠·평면 폴리시', '스프링드라이브·하이비트', '일본 하이엔드 조율'],
    technology: ['스프링드라이브(해당)', '하이비트 오토(해당)', '사파이어', '정밀 조율'],
    priceBand: '교육용 현행 대략 정가: 강철 약 5천~1만 달러대+',
    launchPrice: '교육용 출시 스케일: 1960년 전후 일본 고급 라인 출범. 당시 국내 최고급 정가대. 현행 글로벌 GS 정가(수천달러대+)와 세대가 다름.',
    craft: 'manufacture-finish',
    craftNote: '매뉴팩처급 마감·조율. 산업 라인과 구분되는 등급.',
    metal: '#c8cdd4',
    dial: '#2f5f4a',
    accent: '#d4c4a0',
    alt: '그랜드 세이코 실루엣 스튜디오 누끼',
    commonsPage: 'https://commons.wikimedia.org/wiki/Category:Grand_Seiko',
  }),
  piece({
    id: 'explorer',
    brandId: 'rolex',
    name: 'Explorer',
    nameKo: '익스플로러',
    year: '1953 공개 (에베레스트 탐험과 연결)',
    caliber: '오토매틱 (현행 예: 3230 계열)',
    caseMm: '약 36–40 mm (세대별)',
    summary:
      '가독성을 최우선한 탐험·필드 스포츠 워치. 3·6·9 아라비아 숫자와 메르세데스 시침이 시그니처다.',
    highlights: ['3·6·9 인덱스', '높은 가독성', '오이스터 스포츠 케이스'],
    technology: ['스크류 다운 크라운', '야광 인덱스·침', '방수 스포츠 케이스'],
    priceBand: '교육용 현행 대략 정가: 강철 약 7천~9천 달러대',
    launchPrice: '교육용 출시 스케일: 1953년 전후 탐험·필드. 초기 정가 서브와 비슷한 수백 달러대 인용.',
    craft: 'industrial',
    craftNote: '매뉴팩처 산업 라인.',
    metal: '#c4c8cc',
    dial: '#0d0d0d',
    accent: '#e8e8e8',
    alt: '익스플로러 실루엣 스튜디오 누끼',
    commonsPage: 'https://commons.wikimedia.org/wiki/Category:Rolex_watches',
  }),
  piece({
    id: 'yacht-master',
    brandId: 'rolex',
    name: 'Yacht-Master',
    nameKo: '요트마스터',
    year: '1992 공개',
    caliber: '오토매틱 (현행 예: 3235 계열)',
    caseMm: '약 37–42 mm (라인별)',
    summary:
      '요트·레저용 스포츠 워치. 양방향 회전 베젤(플래티넘·골드 등)과 고급 소재 조합이 다이버와 구분된다.',
    highlights: ['양방향 회전 베젤', '요트·레저 이미지', '소재 조합 베젤'],
    technology: ['양방향 베젤', '스크류 다운 크라운', '야광', '오이스터 케이스'],
    priceBand: '교육용 현행 대략 정가: 강철·플래티넘 베젤 약 1.5만 달러대+',
    launchPrice: '교육용 출시 스케일: 1992년 전후. 서브보다 상위 마린 럭셔리로 출시. 초기 정가 수천달러대 중후반~ 인용(소재별 편차 큼).',
    craft: 'industrial',
    craftNote: '매뉴팩처 산업 라인.',
    metal: '#c9cdd2',
    dial: '#2a2e33',
    accent: '#a8adb4',
    alt: '요트마스터 실루엣 스튜디오 누끼',
    commonsPage: 'https://commons.wikimedia.org/wiki/Category:Rolex_watches',
  }),
  piece({
    id: 'planet-ocean',
    brandId: 'omega',
    name: 'Seamaster Planet Ocean',
    nameKo: '플래닛 오션',
    year: '2005 공개 (시마스터 패밀리)',
    caliber: '코액시얼 오토매틱 (현행 계열)',
    caseMm: '약 39.5–45.5 mm',
    summary:
      '깊은 다이빙용 시마스터 축. 헬륨 탈출 밸브·다이브 베젤·높은 방수 등급이 특징이다.',
    highlights: ['헬륨 밸브', '다이브 베젤', '코액시얼 무브먼트'],
    technology: ['코액시얼', '헬륨 탈출 밸브', '단방향 다이브 베젤', '고방수'],
    priceBand: '교육용 현행 대략 정가: 강철 약 6천~8천 달러대',
    launchPrice: '교육용 출시 스케일: 2005년 전후 시마스터 패밀리 프로 다이버. 출시 정가 대략 수천달러대 중반 인용.',
    craft: 'manufacture-finish',
    craftNote: '오메가 매뉴팩처 라인.',
    metal: '#b8bcc0',
    dial: '#0a0a0a',
    accent: '#e85a1a',
    alt: '플래닛 오션 실루엣 스튜디오 누끼',
    commonsPage: 'https://commons.wikimedia.org/wiki/Category:Omega_watches',
  }),
  piece({
    id: 'de-ville',
    brandId: 'omega',
    name: 'De Ville Prestige',
    nameKo: '드빌 프레스티지',
    year: '1967 드빌 라인 (프레스티지는 후속)',
    caliber: '오토매틱 / 코액시얼 (현행)',
    caseMm: '약 36–41 mm',
    summary:
      '슬림한 드레스·데일리 라인. 썬버스트 다이얼과 리프 핸즈로 「정장용 오메가」 이미지를 만든다.',
    highlights: ['드레스 워치', '썬버스트 다이얼', '슬림 케이스'],
    technology: ['오토매틱', '날짜(해당)', '사파이어', '코액시얼(현행)'],
    priceBand: '교육용 현행 대략 정가: 강철 약 4천~6천 달러대',
    launchPrice: '교육용 출시 스케일: 1960년대 드빌 라인. 초기는 드레스 중상급. 현행 Prestige 출시 정가는 대략 수천달러대.',
    craft: 'manufacture-finish',
    craftNote: '매뉴팩처 생산 드레스 라인.',
    metal: '#cfc8bc',
    dial: '#e8e4dc',
    accent: '#8a7a60',
    alt: '드빌 실루엣 스튜디오 누끼',
    commonsPage: 'https://commons.wikimedia.org/wiki/Category:Omega_watches',
  }),
  piece({
    id: 'twenty-4',
    brandId: 'patek',
    name: 'Twenty~4',
    nameKo: '트웬티포',
    year: '1999 공개',
    caliber: '쿼츠 / 오토매틱 (라인별)',
    caseMm: '직사각·라운드 변형',
    summary:
      '우아한 일일용 라인. 직사각 케이스와 통합 브레이슬릿으로 「주얼리 워치」에 가까운 비율을 만든다.',
    highlights: ['직사각 실루엣', '통합 브레이슬릿', '우아한 데일리'],
    technology: ['쿼츠 또는 오토(라인)', '사파이어', '통합 브레이슬릿'],
    priceBand: '교육용 현행 대략 정가: 강철 수천만 원대+',
    launchPrice: '교육용 출시 스케일: 1999년 전후 여성·슬림 스포츠 드레스. 출시 정가 하이엔드 수천달러대~ 인용.',
    craft: 'manufacture-finish',
    craftNote: '하이엔드 매뉴팩처 시리즈.',
    metal: '#c8ccd2',
    dial: '#1a3a5c',
    accent: '#d4af37',
    alt: '트웬티포 실루엣 스튜디오 누끼',
    commonsPage: 'https://commons.wikimedia.org/wiki/Category:Patek_Philippe',
  }),
  piece({
    id: 'offshore',
    brandId: 'ap',
    name: 'Royal Oak Offshore',
    nameKo: '로열 오크 오프쇼어',
    year: '1993 공개',
    caliber: '크로노그래프 오토매틱 등',
    caseMm: '약 42 mm대+',
    summary:
      '로열 오크의 대담한 스포츠 변형. 팔각 베젤·노출 나사·텍스처 다이얼·크로노 서브다이얼이 시그니처다.',
    highlights: ['오프쇼어 볼륨', '크로노그래프', '고무·텍스처 다이얼'],
    technology: ['크로노그래프', '팔각 베젤', '사파이어', '스포츠 방수'],
    priceBand: '교육용 현행 대략 정가: 강철 수천만 원대+ (시세 변동)',
    launchPrice: '교육용 출시 스케일: 1993년 전후. 로열 오크보다 두껍고 캐주얼한 익스트림 스포츠. 출시 정가 기본 RO보다 높게 잡힌 경우가 많음(수천달러대 후반~).',
    craft: 'manufacture-finish',
    craftNote: '하이엔드 매뉴팩처. 산업 조립에 수작업 마감.',
    metal: '#b0b4b8',
    dial: '#1a1a1a',
    accent: '#e8a838',
    alt: '로열 오크 오프쇼어 실루엣 스튜디오 누끼',
    commonsPage: 'https://commons.wikimedia.org/wiki/Category:Audemars_Piguet',
  }),
  piece({
    id: 'ballon-bleu',
    brandId: 'cartier',
    name: 'Ballon Bleu',
    nameKo: '발롱 블루',
    year: '2007 공개',
    caliber: '오토매틱 / 쿼츠 (라인별)',
    caseMm: '약 33–42 mm',
    summary:
      '둥근 「풍선」 케이스와 카보숑 블루 크라운 가드가 시그니처. 로마 숫자·검(sword) 핸즈의 까르띠에 문법.',
    highlights: ['카보숑 크라운', '라운드 케이스', '로마 인덱스'],
    technology: ['오토매틱(해당)', '사파이어', '크라운 가드'],
    priceBand: '교육용 현행 대략 정가: 강철 약 6천~9천 달러대',
    launchPrice: '교육용 출시 스케일: 2007년 전후. 출시 정가 대략 수천달러대(사이즈·소재별). 주얼리 하우스 중상급 포지션.',
    craft: 'manufacture-finish',
    craftNote: '까르띠에 매뉴팩처·공급 칼리버 혼합 라인.',
    metal: '#d0d4d8',
    dial: '#f2f0ea',
    accent: '#1e4a8c',
    alt: '발롱 블루 실루엣 스튜디오 누끼',
    commonsPage: 'https://commons.wikimedia.org/wiki/Category:Cartier_watches',
  }),
  piece({
    id: 'presage',
    brandId: 'seiko',
    name: 'Presage',
    nameKo: '프레사지',
    year: '2016 라인 강조 (뿌리 더 오래)',
    caliber: '오토매틱 (4R·6R 등)',
    caseMm: '약 38–42 mm',
    summary:
      '일본 오토매틱 드레스·칵테일 라인. 텍스처 다이얼과 도팽 핸즈로 접근성 있는 「아름다운 기계식」을 보여 준다.',
    highlights: ['텍스처 다이얼', '오토매틱 가성비', '드레스·데일리'],
    technology: ['오토매틱', '날짜', '사파이어(상위)', '해커 세컨드(해당)'],
    priceBand: '교육용 현행 대략 정가: 약 4백~1천 달러대+',
    launchPrice: '교육용 출시 스케일: 2016년 전후 라인 강조. 출시 정가 대략 수백~1천달러대(에나멜·특수 마감은 위).',
    craft: 'industrial',
    craftNote: '산업 라인. 다이얼·마감에 수작업 요소가 섞인다.',
    metal: '#c5c0b8',
    dial: '#d8cfc0',
    accent: '#8b6914',
    alt: '프레사지 실루엣 스튜디오 누끼',
    commonsPage: 'https://commons.wikimedia.org/wiki/Category:Seiko_watches',
  }),
  piece({
    id: 'master-ultra',
    brandId: 'jlc',
    name: 'Master Ultra Thin',
    nameKo: '마스터 울트라 씬',
    year: '마스터 컨트롤 패밀리 (울트라씬 계열)',
    caliber: '수동·오토 울트라씬 칼리버',
    caseMm: '약 39–40 mm · 얇은 두께',
    summary:
      '예거의 슬림 드레스 축. 얇은 케이스와 깔끔한 다이얼로 「정밀한 얇음」을 강조한다.',
    highlights: ['울트라씬', '드레스 워치', '마스터 컨트롤 유산'],
    technology: ['울트라씬 무브먼트', '사파이어', '얇은 케이스'],
    priceBand: '교육용 현행 대략 정가: 강철 약 8천~1.2만 달러대',
    launchPrice: '교육용 출시 스케일: 마스터 컨트롤 울트라씬 계열. 현대 출시 정가 대략 수천달러대 중후반~1만달러 전후(모델별).',
    craft: 'manufacture-finish',
    craftNote: '매뉴팩처 마감·조율.',
    metal: '#c8ccd0',
    dial: '#f4f2ec',
    accent: '#2a2a2a',
    alt: '마스터 울트라 씬 실루엣 스튜디오 누끼',
    commonsPage: 'https://commons.wikimedia.org/wiki/Category:Jaeger-LeCoultre',
  }),
  piece({
    id: 'portofino',
    brandId: 'iwc',
    name: 'Portofino Automatic',
    nameKo: '포르토피노',
    year: '1980년대 라인 정착',
    caliber: '오토매틱',
    caseMm: '약 34–40 mm',
    summary:
      '이탈리아 항구 이미지의 클래식 드레스 워치. 깔끔한 다이얼과 리프 핸즈로 데일리·정장용 IWC를 대표한다.',
    highlights: ['클래식 드레스', '간결한 다이얼', '데일리 우아함'],
    technology: ['오토매틱', '날짜', '사파이어'],
    priceBand: '교육용 현행 대략 정가: 강철 약 5천~7천 달러대',
    launchPrice: '교육용 출시 스케일: 1980년대 라인 정착. 드레스 중상급. 현대 오토 출시 정가 대략 수천달러대.',
    craft: 'manufacture-finish',
    craftNote: 'IWC 매뉴팩처·공급 칼리버 라인.',
    metal: '#c4c8cc',
    dial: '#f7f5f0',
    accent: '#1a1a1a',
    alt: '포르토피노 실루엣 스튜디오 누끼',
    commonsPage: 'https://commons.wikimedia.org/wiki/Category:IWC_watches',
  }),
  piece({
    id: 'patrimony',
    brandId: 'vc',
    name: 'Patrimony',
    nameKo: '파트리모니',
    year: '2004 현대 라인 (전통은 더 오래)',
    caliber: '수동·오토 울트라씬',
    caseMm: '약 40 mm · 슬림',
    summary:
      '바쉐론의 미니멀 드레스. 얇은 케이스와 절제된 인덱스로 「고요한 하이엔드」를 보여 준다.',
    highlights: ['미니멀 드레스', '울트라씬', '절제된 다이얼'],
    technology: ['울트라씬 무브먼트', '사파이어', '슬림 케이스'],
    priceBand: '교육용 현행 대략 정가: 강철·골드 수천만 원대+',
    launchPrice: '교육용 출시 스케일: 2004년 전후 현대 라인. 출시 정가 하이엔드 수천~수만달러대(소재·칼리버별).',
    craft: 'manufacture-finish',
    craftNote: '하이엔드 매뉴팩처 마감.',
    metal: '#d0d4d8',
    dial: '#f0eee8',
    accent: '#4a4a4a',
    alt: '파트리모니 실루엣 스튜디오 누끼',
    commonsPage: 'https://commons.wikimedia.org/wiki/Category:Vacheron_Constantin',
  }),
  piece({
    id: 'spirit',
    brandId: 'longines',
    name: 'Spirit',
    nameKo: '스피릿',
    year: '2019 현대 스피릿 (항공 유산)',
    caliber: '오토매틱 (롱진 칼리버)',
    caseMm: '약 40–42 mm',
    summary:
      '항공·탐험 이미지의 파일럿 워치. 아라비아 숫자·카시드럴 핸즈·플루티드 크라운이 특징이다.',
    highlights: ['파일럿 스타일', '아라비아 숫자', '플루티드 크라운'],
    technology: ['오토매틱', '날짜', '사파이어', '방수 스포츠 케이스'],
    priceBand: '교육용 현행 대략 정가: 강철 약 2천~3천 달러대',
    launchPrice: '교육용 출시 스케일: 2019년 전후 현대 Spirit. 출시 정가 대략 2천~3천달러대 인용(강철 기준).',
    craft: 'industrial',
    craftNote: '스위스 산업 라인. 접근성 있는 매뉴팩처 칼리버.',
    metal: '#b8bcc2',
    dial: '#1a3a6a',
    accent: '#e8e8e8',
    alt: '스피릿 실루엣 스튜디오 누끼',
    commonsPage: 'https://commons.wikimedia.org/wiki/Category:Longines_watches',
  }),
];

export function brandById(id: string): Brand | undefined {
  return brands.find((b) => b.id === id);
}

export function pieceById(id: string): WatchPiece | undefined {
  return pieces.find((p) => p.id === id);
}

export function piecesForBrand(brandId: string): WatchPiece[] {
  return pieces.filter((p) => p.brandId === brandId);
}
